import { useState } from "react";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Search, UserPlus, Users, Check, X, FolderOpen, EyeOff, RotateCcw, Trash2, Pencil, AlertTriangle } from "lucide-react";
import { useFriends, useReceivedInvitations, useSentInvitations, useAcceptInvitation, useRejectInvitation, useCancelInvitation, useResendInvitation, useHiddenFriends, useUnhideFriend, useFriendGroups, useDeleteFriendGroup, useUpdateFriendInvitation, useRemoveFriend } from "@/lib/queries/community";
import { useToast } from "@/hooks/use-toast";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Link } from "wouter";

type FriendIdentity = {
  firstName?: string | null;
  lastName?: string | null;
  email?: string | null;
};

export function getFriendDisplayName(friend: FriendIdentity | null | undefined, alias?: string | null) {
  const profileName = [friend?.firstName, friend?.lastName]
    .filter((part): part is string => typeof part === "string" && part.trim().length > 0)
    .map((part) => part.trim())
    .join(" ");
  const cleanAlias = alias?.trim();

  if (cleanAlias) {
    return profileName && profileName !== cleanAlias
      ? `${cleanAlias} (${profileName})`
      : cleanAlias;
  }

  return profileName || friend?.email?.trim() || "Unnamed friend";
}

export function PeopleView() {
  const [activeTab, setActiveTab] = useState("friends");

  const { data: friends = [] } = useFriends();
  const { data: hiddenFriends = [] } = useHiddenFriends();
  const { data: receivedInvitations = [] } = useReceivedInvitations();
  const { data: sentInvitations = [] } = useSentInvitations();
  const { data: friendGroups = [] } = useFriendGroups();

  const pendingReceived = receivedInvitations.filter((i) => i.status === "pending");
  const activeSent = sentInvitations.filter((i) => i.status === "pending" || i.status === "expired");

  return (
    <div className="flex-1 flex flex-col h-full bg-background min-w-0">
      <div className="px-6 py-4 border-b shrink-0">
        <h2 className="text-xl font-bold flex items-center gap-2">
          <Users className="w-5 h-5 text-primary" />
          People
        </h2>
        <p className="text-sm text-muted-foreground mt-1">Manage your friends, groups, and invitations.</p>
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab} className="flex-1 flex flex-col min-h-0">
        <div className="px-6 pt-4 shrink-0 overflow-x-auto">
          <TabsList className="w-auto inline-flex justify-start h-9 min-w-min">
            <TabsTrigger value="friends" className="text-xs h-7 whitespace-nowrap" data-testid="tab-communicate-friends">
              Friends ({friends.length})
            </TabsTrigger>
            <TabsTrigger value="groups" className="text-xs h-7 whitespace-nowrap" data-testid="tab-communicate-friend-groups">
              Friend Groups ({friendGroups.length})
            </TabsTrigger>
            <TabsTrigger value="invitations" className="text-xs h-7 whitespace-nowrap" data-testid="tab-communicate-invitations">
              Invitations {pendingReceived.length > 0 && <Badge variant="secondary" className="ml-2 text-[10px] h-4 px-1">{pendingReceived.length}</Badge>}
            </TabsTrigger>
          </TabsList>
        </div>

        <ScrollArea className="flex-1 px-6 py-4">
          <TabsContent value="friends" className="m-0 space-y-6">
            <FriendsList friends={friends} hiddenFriends={hiddenFriends} />
          </TabsContent>

          <TabsContent value="groups" className="m-0 space-y-6">
            <FriendGroupsList groups={friendGroups} />
          </TabsContent>

          <TabsContent value="invitations" className="m-0 space-y-6">
            <InvitationsList received={pendingReceived} sent={activeSent} />
          </TabsContent>
        </ScrollArea>
      </Tabs>
    </div>
  );
}

function FriendGroupsList({ groups }: { groups: any[] }) {
  const { toast } = useToast();
  const deleteMutation = useDeleteFriendGroup();

  if (groups.length === 0) {
    return (
      <div className="text-center py-8 text-muted-foreground text-sm border rounded-lg bg-muted/20">
        No friend groups created yet.
      </div>
    );
  }

  return (
    <div className="grid gap-3">
      {groups.map(group => (
        <Card key={group.id} className="p-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <FolderOpen className="w-5 h-5 text-muted-foreground" />
            <div>
              <p className="font-medium text-sm">{group.name}</p>
            </div>
          </div>
          <Button size="sm" variant="ghost" onClick={async () => {
            await deleteMutation.mutateAsync(group.id);
            toast({ title: "Group deleted" });
          }}>
            <Trash2 className="w-4 h-4 text-destructive" />
          </Button>
        </Card>
      ))}
    </div>
  );
}

function FriendsList({ friends, hiddenFriends }: { friends: any[]; hiddenFriends: any[] }) {
  const [search, setSearch] = useState("");
  const { toast } = useToast();
  const unhideFriendMutation = useUnhideFriend();
  const removeFriendMutation = useRemoveFriend();

  const filtered = friends.filter((f) => {
    if (!search) return true;
    const s = search.toLowerCase();
    return (
      (f.friend?.firstName || "").toLowerCase().includes(s) ||
      (f.friend?.lastName || "").toLowerCase().includes(s) ||
      (f.friend?.email || "").toLowerCase().includes(s) ||
      (f.alias || "").toLowerCase().includes(s)
    );
  });

  return (
    <div className="space-y-4">
      <div className="relative max-w-md">
        <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
        <Input
          placeholder="Search friends..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="pl-9 h-9 text-sm"
        />
      </div>

      {filtered.length === 0 ? (
        <div className="text-center py-8 text-muted-foreground text-sm border rounded-lg bg-muted/20">
          No active friends found.
        </div>
      ) : (
        <div className="grid gap-3">
          {filtered.map((f) => (
            <Card key={f.id} className="p-4 flex items-center justify-between gap-4">
              <div>
                <p className="font-medium text-sm">
                   {getFriendDisplayName(f.friend, f.alias)}
                </p>
                <p className="text-xs text-muted-foreground">{f.friend.email}</p>
                <div className="mt-2" data-testid={`friend-individual-shares-${f.id}`}>
                  <p className="text-xs font-medium">Individual shares only</p>
                  <p className="text-xs text-muted-foreground">This friendship provides no blanket module access. Choose a specific record and use its Share control.</p>
                  <div className="flex flex-wrap gap-2 mt-1">
                    {[
                      ["Notes/Notepad", "/notepad"], ["Goals", "/goals"], ["Calendar", "/calendar"], ["Tasks", "/tasks"], ["Habits", "/habits"], ["Tracker-X", "/trackerx"],
                    ].map(([label, href]) => <Link key={href} href={href} data-testid={`link-friend-${f.id}-${label.toLowerCase()}`} className="text-xs text-primary hover:underline">{label}</Link>)}
                  </div>
                </div>
              </div>
              <Button size="sm" variant="destructive" data-testid={`button-remove-friend-${f.id}`} disabled={removeFriendMutation.isPending} onClick={async () => {
                try {
                  await removeFriendMutation.mutateAsync(f.id);
                  toast({ title: "Friend removed" });
                } catch (error: any) {
                  toast({ title: "Unable to remove friend", description: error?.message, variant: "destructive" });
                }
              }}><Trash2 className="w-4 h-4 mr-1" />Remove</Button>
            </Card>
          ))}
        </div>
      )}

      {hiddenFriends.length > 0 && (
        <div className="mt-8">
          <h3 className="text-sm font-medium mb-3 flex items-center gap-2">
            <EyeOff className="w-4 h-4 text-muted-foreground" />
            Hidden Friends ({hiddenFriends.length})
          </h3>
          <div className="grid gap-3 opacity-75">
            {hiddenFriends.map((f) => (
              <Card key={f.id} className="p-4 flex items-center justify-between">
                <div>
                  <p className="font-medium text-sm">
                     {getFriendDisplayName(f.friend, f.alias)}
                  </p>
                  <p className="text-xs text-muted-foreground">{f.friend.email}</p>
                </div>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={async () => {
                    await unhideFriendMutation.mutateAsync(f.id);
                    toast({ title: "Friend restored" });
                  }}
                >
                  <RotateCcw className="w-3.5 h-3.5 mr-1" />
                  Restore
                </Button>
              </Card>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

function InvitationsList({ received, sent }: { received: any[]; sent: any[] }) {
  const { toast } = useToast();
  const acceptMutation = useAcceptInvitation();
  const rejectMutation = useRejectInvitation();
  const cancelMutation = useCancelInvitation();
  const resendMutation = useResendInvitation();
  const updateMutation = useUpdateFriendInvitation();
  const [editingInvitationId, setEditingInvitationId] = useState<string | null>(null);
  const [editingDisplayName, setEditingDisplayName] = useState("");
  const relationshipLabel = (value?: string) => value === "family" ? "Family member" : "Friend";

  const handleResend = async (invitationId: string) => {
    try {
      const result: any = await resendMutation.mutateAsync(invitationId);
      if (result?.emailSent === false) {
        toast({
          title: "Email could not be delivered",
          description: "A new invitation link was created, but the email failed to send. The old link no longer works — please try resending again.",
          variant: "destructive",
        });
      } else {
        toast({ title: "Invitation resent" });
      }
    } catch (err: any) {
      toast({ title: "Failed to resend invitation", description: err?.message, variant: "destructive" });
    }
  };

  const handleSaveEdit = async (invitationId: string) => {
    try {
      await updateMutation.mutateAsync({
        invitationId,
        displayName: editingDisplayName.trim() || undefined,
      });
      setEditingInvitationId(null);
      toast({ title: "Invitation updated" });
    } catch (err: any) {
      toast({ title: "Failed to update invitation", description: err?.message, variant: "destructive" });
    }
  };

  return (
    <div className="space-y-8">
      <div>
        <h3 className="text-sm font-medium mb-3">Send Invitation</h3>
        <p className="text-sm text-muted-foreground mb-2">Invite a friend to connect. Records are shared individually after acceptance.</p>
        <Link href="/communicate?invite=friend">
          <Button data-testid="button-open-friend-invite-wizard"><UserPlus className="w-4 h-4 mr-2" /> Invite Friend</Button>
        </Link>
      </div>

      <div>
        <h3 className="text-sm font-medium mb-3">Received Invitations</h3>
        {received.length === 0 ? (
          <div className="text-center py-6 text-muted-foreground text-sm border rounded-lg bg-muted/20">
            No pending received invitations.
          </div>
        ) : (
          <div className="grid gap-3">
            {received.map((inv) => (
              <Card key={inv.id} className="p-4 flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                <div>
                  <p className="font-medium text-sm">{inv.fromUser?.firstName} {inv.fromUser?.lastName}</p>
                  <p className="text-xs text-muted-foreground">{inv.fromUser?.email}</p>
                  <Badge variant="secondary" className="mt-1">{relationshipLabel(inv.relationshipType)}</Badge>
                  <p className="text-xs text-muted-foreground mt-1" data-testid={`text-received-invitation-access-${inv.id}`}>No blanket module access; records must be shared individually.</p>
                </div>
                <div className="flex items-center gap-2">
                  <Button size="sm" onClick={() => acceptMutation.mutate(inv.id)}>
                    <Check className="w-4 h-4 mr-1" /> Accept
                  </Button>
                  <Button size="sm" variant="outline" onClick={() => rejectMutation.mutate(inv.id)}>
                    <X className="w-4 h-4 mr-1" /> Reject
                  </Button>
                </div>
              </Card>
            ))}
          </div>
        )}
      </div>

      <div>
        <h3 className="text-sm font-medium mb-3">Sent Invitations</h3>
        {sent.length === 0 ? (
          <div className="text-center py-6 text-muted-foreground text-sm border rounded-lg bg-muted/20">
            No pending sent invitations.
          </div>
        ) : (
          <div className="grid gap-3">
            {sent.map((inv) => (
              <Card key={inv.id} className="p-4 flex flex-col sm:flex-row sm:items-center justify-between gap-4" data-testid={`invited-friend-${inv.id}`}>
                <div>
                  <p className="font-medium text-sm">{inv.inviteeEmail}</p>
                  {inv.displayName && <p className="text-sm">{inv.displayName}</p>}
                  <Badge variant="secondary" className="mt-1">{relationshipLabel(inv.relationshipType)}</Badge>
                  <p className="text-xs text-muted-foreground">Sent: {new Date(inv.createdAt).toLocaleDateString()}</p>
                   <p className="text-xs text-muted-foreground mt-1" data-testid={`text-sent-invitation-access-${inv.id}`}>No blanket module access; records must be shared individually.</p>
                  <Badge
                    variant="outline"
                    className={inv.status === "expired" ? "mt-2 border-dashed" : "mt-2"}
                    data-testid={`invited-friend-status-${inv.id}`}
                  >
                    {inv.status === "expired" && <AlertTriangle className="w-3 h-3 mr-1" />}
                    {inv.status}
                  </Badge>
                </div>
                {editingInvitationId === inv.id ? (
                  <div className="flex items-center gap-2">
                    <Input
                      value={editingDisplayName}
                      onChange={(event) => setEditingDisplayName(event.target.value)}
                      placeholder="Display name"
                      data-testid="input-edit-display-name"
                    />
                    <Button
                      size="sm"
                      onClick={() => handleSaveEdit(inv.id)}
                      disabled={updateMutation.isPending}
                      data-testid="button-save-edit"
                    >
                      Save
                    </Button>
                  </div>
                ) : (
                  <div className="flex items-center gap-2">
                    {inv.status === "pending" && (
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => {
                          setEditingInvitationId(inv.id);
                          setEditingDisplayName(inv.displayName ?? "");
                        }}
                        data-testid={`button-edit-invitation-${inv.id}`}
                      >
                        <Pencil className="w-4 h-4" />
                      </Button>
                    )}
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => handleResend(inv.id)}
                      data-testid={`button-resend-invite-${inv.id}`}
                    >
                      Resend
                    </Button>
                    <Button size="sm" variant="destructive" onClick={() => cancelMutation.mutate(inv.id)}>
                      Cancel
                    </Button>
                  </div>
                )}
              </Card>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
